import { useState } from 'react';
import { REPORT_POSITION_ORDER } from '../../lib/field-time-report';
import { WizardBar, WizardHead, WizardFoot, WizardList } from './WizardChrome';

// Season playing time, for a coach who wants to spot incongruities himself.
//
// The old Field Time tab was a wide position matrix. It answers "who plays where"
// well, but the fairness question is "who has been shorted", and in a matrix that
// answer is buried in one column you have to scan across nine others to reach.
// So the DEFAULT view is ranked by bench share, most benched first, and the matrix
// survives intact behind the "By position" toggle.
//
// Advisory only. Nothing on this screen mutates the plan.

/** Matches the DUE rule already shipping in the Field Time tab. */
export const DUE_BENCH_SHARE = 0.4;
export const DUE_MIN_INNINGS = 6;

export interface FieldTimeRow {
  player: any;
  field: number;
  bench: number;
  total: number;
  share: number;   // bench share, 0-1
  due: boolean;
  positions: Record<string, number>;
  gp: number;
}

/** Shared by this screen and the defense step's "running light" count. */
export function buildFieldTimeRows(
  players: any[],
  playerFieldTimeStats: Map<string, any> | undefined,
): FieldTimeRow[] {
  return players.map((p: any) => {
    const ft = playerFieldTimeStats?.get(p.id);
    const positions: Record<string, number> = ft?.positions ?? {};
    const field = Object.values(positions).reduce((a: number, b) => a + (Number(b) || 0), 0);
    const bench = ft?.bench ?? 0;
    const total = field + bench;
    const share = total > 0 ? bench / total : 0;
    return {
      player: p, field, bench, total, share,
      due: share > DUE_BENCH_SHARE && total >= DUE_MIN_INNINGS,
      positions, gp: ft?.gp ?? 0,
    };
  }).sort((a, b) => b.share - a.share || b.bench - a.bench);
}

export const SeasonFieldTime = ({ players, playerFieldTimeStats, team, gamesCounted, onBack }: {
  players: any[];
  playerFieldTimeStats: Map<string, any> | undefined;
  team: any;
  gamesCounted: number;
  onBack: () => void;
}) => {
  const [view, setView] = useState<'player' | 'position'>('player');
  const rows = buildFieldTimeRows(players, playerFieldTimeStats);
  const anyData = rows.some(r => r.total > 0);

  const positions = REPORT_POSITION_ORDER.filter(pos => {
    if (pos === 'P' && team.settings?.noPitcher) return false;
    if (pos === 'C' && team.settings?.noCatcher) return false;
    if ((pos === 'LCF' || pos === 'RCF') && !team.settings?.fourOutfielders) return false;
    if (pos === 'CF' && team.settings?.fourOutfielders) return false;
    return true;
  });

  return (
    <>
      <WizardBar label="Season field time" onBack={onBack} />
      <WizardHead
        title="Who is running light"
        sub={anyData
          ? `${gamesCounted} game${gamesCounted === 1 ? '' : 's'} logged. Sorted by bench share, most benched first.`
          : 'No innings logged yet. Field time appears once you run a game.'}
      />

      <div className="flex-1 overflow-y-auto bg-canvas px-4 pt-4">
        <div className="flex bg-raised rounded-control p-[3px] mb-3.5">
          {(['player', 'position'] as const).map(v => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`flex-1 text-center text-sm font-semibold py-1.5 rounded-[8px] ${
                view === v ? 'bg-surface text-ink shadow-sm' : 'text-ink-2'
              }`}
            >
              {v === 'player' ? 'By player' : 'By position'}
            </button>
          ))}
        </div>

        {view === 'player' ? (
          <>
            <WizardList>
              {rows.map((r, i) => (
                <div key={r.player.id} className={`px-4 py-3 ${i === rows.length - 1 ? '' : 'border-b border-hairline'}`}>
                  <div className="flex items-baseline gap-2">
                    <span className="flex-1 min-w-0 text-[15.5px] font-semibold tracking-[-0.012em] text-ink truncate">
                      {r.player.name}
                    </span>
                    {r.due && (
                      <span className="text-[10px] font-bold tracking-[0.06em] text-gold-ink bg-gold-soft px-1.5 py-0.5 rounded shrink-0">
                        DUE
                      </span>
                    )}
                    <span className="text-[13.5px] text-ink-2 tabular-nums shrink-0">
                      {r.total > 0 ? `${r.field} field, ${r.bench} bench` : 'no innings yet'}
                    </span>
                  </div>
                  <div className="flex h-2 rounded-full overflow-hidden mt-2 bg-raised">
                    <span className="bg-accent" style={{ width: `${r.total > 0 ? (r.field / r.total) * 100 : 0}%` }} />
                    <span className="bg-ink-mute" style={{ width: `${r.total > 0 ? (r.bench / r.total) * 100 : 0}%` }} />
                  </div>
                </div>
              ))}
            </WizardList>
            <div className="flex gap-4 px-1 pt-3 text-xs text-ink-2">
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-2.5 h-2.5 rounded-sm bg-accent" />On the field
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-2.5 h-2.5 rounded-sm bg-ink-mute" />Bench
              </span>
            </div>
          </>
        ) : (
          <div className="bg-surface rounded-panel overflow-x-auto">
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr className="border-b border-hairline">
                  <th className="text-left px-3.5 py-2 text-[9.5px] font-bold uppercase tracking-[0.05em] text-ink-2 sticky left-0 bg-surface min-w-[92px]">
                    Player
                  </th>
                  <th className="px-1.5 py-2 text-[9.5px] font-bold uppercase tracking-[0.05em] text-ink-2">GP</th>
                  <th className="px-1.5 py-2 text-[9.5px] font-bold uppercase tracking-[0.05em] text-ink-2">BN</th>
                  {positions.map(pos => (
                    <th key={pos} className="px-1.5 py-2 text-[9.5px] font-bold uppercase tracking-[0.05em] text-ink-2">
                      {pos}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.player.id} className="border-b border-hairline last:border-0">
                    <td className="px-3.5 py-2.5 text-[13px] font-semibold text-ink sticky left-0 bg-surface truncate max-w-[92px]">
                      {r.player.name.trim().split(' ').filter(Boolean).pop() || r.player.name}
                    </td>
                    <td className="px-1.5 py-2.5 text-center tabular-nums text-ink-2">{r.gp || '–'}</td>
                    <td className={`px-1.5 py-2.5 text-center tabular-nums ${r.due ? 'text-danger font-bold' : 'text-ink-2'}`}>
                      {r.bench || '–'}
                    </td>
                    {positions.map(pos => {
                      const n = r.positions[pos] ?? 0;
                      const heavy = r.field > 0 && n / r.field > 0.4;
                      return (
                        <td
                          key={pos}
                          className={`px-1.5 py-2.5 text-center tabular-nums ${
                            n === 0 ? 'text-ink-mute/40' : heavy ? 'text-ink font-bold' : 'text-ink-2'
                          }`}
                        >
                          {n === 0 ? '–' : n}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="h-4" />
      </div>

      <WizardFoot primaryLabel="Back to defense" onPrimary={onBack} />
    </>
  );
};
